import { Link } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";

import type { OrganizationSummary, UserSummary } from "../../shared/schemas";
import { Brand } from "./Brand";
import { PrimaryNavigation, type ShellNavigationItem } from "./PrimaryNavigation";
import type { ShellViewMode } from "./ShellViewModeProvider";
import { ThemeToggle } from "./ThemeToggle";
import { UserProfileMenu } from "./UserProfileMenu";
import { buttonVariants } from "./ui/button";

interface ShellHeaderProps {
  activeNavigationItem: ShellNavigationItem | null;
  activeOrganization: OrganizationSummary | undefined;
  activeOrganizationId: string | undefined;
  backToToolsOrganizationId: string | undefined;
  isOnline: boolean;
  isSigningOut: boolean;
  organizations: OrganizationSummary[];
  user: UserSummary;
  viewMode: ShellViewMode;
  onOrganizationChange: (organizationId: string) => void;
  onSignOut: () => void;
  onViewModeChange: (viewMode: ShellViewMode) => void;
}

function ShellBrandGroup({
  activeOrganization,
  activeOrganizationId,
  backToToolsOrganizationId,
}: Pick<
  ShellHeaderProps,
  "activeOrganization" | "activeOrganizationId" | "backToToolsOrganizationId"
>) {
  const workspace = activeOrganization?.logoVersion
    ? {
        name: activeOrganization.name,
        logoSrc: `/api/workspaces/${encodeURIComponent(activeOrganization.id)}/logo?v=${activeOrganization.logoVersion}`,
      }
    : undefined;
  return (
    <div className="shell-brand-group">
      {backToToolsOrganizationId ? (
        <Link
          className={buttonVariants({
            variant: "ghost",
            size: "sm",
            className: "min-h-10 pr-3 pl-2.5",
          })}
          search={{ org: backToToolsOrganizationId }}
          to="/"
        >
          <ArrowLeftIcon aria-hidden="true" data-icon="inline-start" />
          All tools
        </Link>
      ) : null}
      <Link
        aria-label={workspace ? `${workspace.name} workspace home` : "CCBox home"}
        className="shell-brand-link flex min-h-10 items-center rounded-sm no-underline"
        search={{ org: activeOrganizationId }}
        to="/"
      >
        <Brand {...(workspace ? { workspace } : {})} />
      </Link>
    </div>
  );
}

export function ShellHeader(props: ShellHeaderProps) {
  return (
    <header className="shell-header sticky top-0 z-20 border-b bg-card">
      <div className="shell-header-inner">
        <ShellBrandGroup
          activeOrganization={props.activeOrganization}
          activeOrganizationId={props.activeOrganizationId}
          backToToolsOrganizationId={props.backToToolsOrganizationId}
        />
        <PrimaryNavigation
          activeItem={props.activeNavigationItem}
          adminView={props.viewMode === "admin"}
          organizationId={props.activeOrganizationId}
        />
        <div className="shell-account-controls">
          <ThemeToggle />
          <div className="mx-1 hidden h-7 w-px bg-border sm:block" />
          <UserProfileMenu
            activeOrganizationId={props.activeOrganizationId}
            isOnline={props.isOnline}
            isSigningOut={props.isSigningOut}
            organizations={props.organizations}
            user={props.user}
            viewMode={props.viewMode}
            onOrganizationChange={props.onOrganizationChange}
            onSignOut={props.onSignOut}
            onViewModeChange={props.onViewModeChange}
          />
        </div>
      </div>
    </header>
  );
}
